import React, { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactModal from "react-modal";
import { IoCloseOutline } from "react-icons/io5";

import CustomTable from "../../components/universal/CustomTable";
import ConfirmToast from "../../components/universal/ConfirmToast";
import PlanModal from "./PlanModal";
import SermonPlanModal from "./SermonPlanModal";
import { AllPlansHooks, handleDeletePlan } from "./hooks/PlanHooks";
import {
  fetchCmsSermonStudies,
  fetchGlobalSermonStudies,
  attachGlobalSermonStudy,
  detachGlobalSermonStudy,
} from "../../store/SermonStudies/sermonStudiesAPI";
import sermonStudyPlaceholderImage from "../../assets/images/bibleplan.jpg";

const allPlansHeader = [
  { headerName: "Plan Name", headerId: "planName", width: "w-48" },
  { headerName: "Description", headerId: "planDescription", width: "w-64" },
  { headerName: "Status", headerId: "sermonStatus", width: "w-36" },
  { headerName: "Source", headerId: "sermonSource", width: "w-40" },
  { headerName: "Actions", headerId: "actions", width: "w-32" },
];

//function to validate image url before rendering
const isRenderableImageUrl = (value) => typeof value === "string" && /^https?:\/\//i.test(value);

//function to extract a YouTube video id from common URL formats
const getYouTubeVideoId = (url = "") => {
  if (!url || typeof url !== "string") return "";

  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&?/]+)/
  );

  return match?.[1] || "";
};

//function to resolve global sermon study thumbnail using the shared media contract
const getSermonStudyThumbnailUrl = (sermonStudy = {}) => {
  const plan = sermonStudy?.plan || {};
  const gallery = sermonStudy?.gallery || plan?.gallery || {};
  const candidates = [
    gallery?.imageUrl,
    gallery?.mobileImageUrl,
    gallery?.tabImageUrl,
    gallery?.galleryUrl,
    gallery?.url_link,
    gallery?.url,
    gallery?.uri,
    gallery?.path,
    plan?.plansCoverImageUrl,
    sermonStudy?.plansCoverImageUrl,
    sermonStudy?.planThumbnailUrl,
    plan?.planThumbnailUrl,
    sermonStudy?.thumbnailUrl,
    plan?.thumbnailUrl,
    sermonStudy?.thumbnail,
  ];

  const directImageUrl = candidates.find(isRenderableImageUrl);
  if (directImageUrl) return directImageUrl;

  const youtubeVideoId = getYouTubeVideoId(sermonStudy?.youtubeLink || plan?.youtubeLink);
  return youtubeVideoId ? `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg` : "";
};

//function to identify TWG/global attached sermon studies
const isTWGContentRow = (row = {}) => {
  return row.source === "attachedFromGlobal" || row.attachedFromGlobal === true || !!row.sourceSermonStudyObjId;
};

const SermonPlans = ({ church, typeofentity }) => {
  const { reloadData, setReloadData, dispatch } =
    AllPlansHooks();
  const [isSermonModalOpen, setIsSermonModalOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // States for Global Sermon Study attachment
  const [isTWGModalOpen, setIsTWGModalOpen] = useState(false);
  const [twgPlans, setTwgPlans] = useState([]);
  const [selectedTWGPlans, setSelectedTWGPlans] = useState([]);
  const [isFetchingTWG, setIsFetchingTWG] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);

  const getTargetEntityType = () => {
    if (typeofentity) return String(typeofentity);
    if (church?.typeofentity) return String(church.typeofentity);
    if (church?._id) return "1";
    return "";
  };

  const getTargetEntityId = () => {
    return church?._id || church?.entityObjId || church?.churchObjId || "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const transformTableData = (data) =>
    data.map((item) => {
      const plan = item.plan || item;
      const status = item.lifecycleStatus || item.status || "";
      const source = item.source === "attachedFromGlobal" ? "TWG Content" : item.scope === "global" ? "Global" : "Created";

      return {
        ...plan,
        ...item,
        planName: item.planName || plan.planName || "Untitled Plan",
        planDescription: item.planDescription || plan.planDescription || plan.description || item.description || item.finalLogMessage || "No description",
        sermonStatus: status || "N/A",
        sermonSource: source,
        noOfDays: plan.noOfDays || plan.noofDays || item.noofDays || "0",
        createdDate: formatDate(item.attachedAt || item.createdDate || plan.createdDate),
        churchId: item.entityObjId || plan.churchObjId || plan.channelObjId || plan.ministriesObjId || church?._id || "",
        planObjId: item.targetPlanObjId || plan._id || item.syncedToPlanId || "",
        isTWGContent: item.source === "attachedFromGlobal" || plan.attachedFromGlobal === true || !!item.sourceSermonStudyObjId,
        galleryObjId: plan.galleryObjId || item.galleryObjId,
        gallery: plan.gallery || item.gallery,
        dayPlans: plan.dayPlans || plan.dayplans || [],
      };
    });

  //function to detach attached TWG sermon study from current entity
  const handleDetachTWGSermonStudy = async (row, onClose) => {
    try {
      const res = await detachGlobalSermonStudy({
        sourceSermonStudyObjId: row.sourceSermonStudyObjId,
        targetPlanObjId: row.targetPlanObjId || row.planObjId,
        typeofentity: row.typeofentity || getTargetEntityType(),
        entityObjId: row.entityObjId || getTargetEntityId(),
      });

      if (Number(res.type) === 1) {
        toast.success("TWG sermon study detached successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setReloadData((prev) => !prev);
      } else {
        toast.error(res.message || "Failed to detach TWG sermon study!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Failed to detach TWG sermon study!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      onClose();
    }
  };

  const fetchCmsSermonStudyPlans = async (page, limit, searchCriteria = {}, callback) => {
    try {
      const res = await fetchCmsSermonStudies({
        typeofentity: getTargetEntityType(),
        entityObjId: getTargetEntityId(),
        searchText: searchCriteria?.searchText || "",
        page,
        limit
      });

      callback?.({
        data: Number(res.type) === 1 ? res.data || [] : [],
        total: Number(res.type) === 1 ? res.totalRecords || 0 : 0,
        page,
        limit,
      });
    } catch (error) {
      callback?.({ data: [], total: 0, page, limit });
    }
  };
  const handleSelectTWGPlanClick = async () => {
    setIsFetchingTWG(true);
    setIsTWGModalOpen(true);
    setSelectedTWGPlans([]);

    try {
      const res = await fetchGlobalSermonStudies({
        typeofentity: getTargetEntityType(),
        entityObjId: getTargetEntityId(),
        page: 1,
        limit: 100
      });

      if (Number(res.type) === 1) {
        setTwgPlans(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch global sermon studies");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to fetch global sermon studies");
    } finally {
      setIsFetchingTWG(false);
    }
  };

  const togglePlanSelection = (plan) => {
    if (plan?.isAttachedToCurrentEntity) return;

    const planId = plan?._id;
    if (!planId) return;

    setSelectedTWGPlans(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      }
      return [...prev, planId];
    });
  };

  const handleCloseTWGModal = () => {
    setIsTWGModalOpen(false);
    setSelectedTWGPlans([]);
  };

  const handleSaveSelectedPlans = async () => {
    if (selectedTWGPlans.length === 0) return;

    const targetEntityId = getTargetEntityId();
    const targetEntityType = getTargetEntityType();

    if (!targetEntityId || !targetEntityType) {
      toast.error("Please select a Church, Channel, or Ministry before attaching global sermon studies.");
      return;
    }

    setIsSavingLinks(true);
    let successCount = 0;
    let failCount = 0;

    for (const sermonStudyId of selectedTWGPlans) {
      const selectedStudy = twgPlans.find((item) => item._id === sermonStudyId);
      const payload = {
        sourceSermonStudyObjId: sermonStudyId,
        sourcePlanObjId: selectedStudy?.sourcePlanObjId || selectedStudy?.plan?._id || "",
        typeofentity: targetEntityType,
        entityObjId: targetEntityId
      };

      try {
        const res = await attachGlobalSermonStudy(payload);
        if (Number(res.type) === 1) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    setIsSavingLinks(false);
    if (successCount > 0) {
      toast.success(`${successCount} global sermon stud${successCount === 1 ? "y" : "ies"} attached successfully!`);
      setSelectedTWGPlans([]);
      setIsTWGModalOpen(false);
      setReloadData(prev => !prev);
    }
    if (failCount > 0) {
      toast.error(`Failed to attach ${failCount} sermon stud${failCount === 1 ? "y" : "ies"}.`);
    }
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden bg-[#191919] min-h-0">
      <div className="relative p-4 max-w-full overflow-x-hidden">
        <div className="flex justify-end items-center mb-6 max-w-full">
          <div className="flex flex-wrap justify-end gap-3 max-w-full">
            <button
              onClick={handleSelectTWGPlanClick}
              className="flex items-center gap-2 text-[13px] text-yellowish border px-6 py-2 border-yellowish bg-blackish rounded-xl hover:bg-yellowish hover:text-black transition-colors"
            >
              Attach Global Sermon Study
            </button>
            <button
              onClick={() => setIsSermonModalOpen(true)}
              className="flex items-center gap-2 text-[13px] text-yellowish border px-6 py-2 border-yellowish bg-blackish rounded-xl hover:bg-yellowish hover:text-black transition-colors"
            >
              <AiOutlinePlus /> Add Sermon Plan
            </button>
          </div>
        </div>

        {!isPlanModalOpen && !isSermonModalOpen && (
          <CustomTable
            searchFor="Plans"
            heightDesktop="h-[68vh]"
            width="w-full"
            nthChildWidth="[&>*:nth-child(1)]:w-44 [&>*:nth-child(2)]:w-56 [&>*:nth-child(3)]:w-28 [&>*:nth-child(4)]:w-36 [&>*:nth-child(5)]:w-28"
            notificationTable={true}
            headers={allPlansHeader}
            data={[]}
            customDataFetch={true}
            customDataFetchFunction={fetchCmsSermonStudyPlans}
            dataTransformer={transformTableData}
            reloadData={reloadData}
            setReloadData={setReloadData}
            reverseTable={false}
            handleDeleteClick={(row) => {
              const isTWGContent = isTWGContentRow(row);
              confirmAlert({
                customUI: ({ onClose }) => (
                  <ConfirmToast
                    title={isTWGContent ? "Detach TWG Sermon Study" : "Delete Plan"}
                    message={
                      isTWGContent
                        ? `Detach "${row.planName}" from this entity? The original TWG content will remain available globally.`
                        : `Are you sure you want to delete "${row.planName}"? This action cannot be undone.`
                    }
                    deleteButtonText={isTWGContent ? "Yes, Detach it!" : "Yes, Delete it!"}
                    onClose={onClose}
                    onSuccess={() => {
                      if (isTWGContent) {
                        handleDetachTWGSermonStudy(row, onClose);
                        return;
                      }

                      handleDeletePlan(
                        dispatch,
                        row.churchId,
                        row.planObjId,
                        (res) => {
                          if (res.type === 1) {
                            toast.success("Plan deleted successfully!", {
                              position: "top-right",
                              autoClose: 3000,
                            });
                            setReloadData((prev) => !prev);
                          } else {
                            toast.error(
                              res.message || "Failed to delete plan!",
                              {
                                position: "top-right",
                                autoClose: 3000,
                              }
                            );
                          }
                          onClose();
                        }
                      );
                    }}
                  />
                ),
              });
            }}
            handleEditClick={(row) => {
              if (isTWGContentRow(row)) return;
              setSelectedRow(row);
              setIsPlanModalOpen(true);
            }}
            shouldShowEdit={(row) => !isTWGContentRow(row)}
            showViewDetails={true}
            handleViewDetailsClick={(row) => {
                            toast.info(`Viewing details for "${row.planName}"`, {
                position: "top-right",
                autoClose: 2000,
              });
            }}
            emptyStateMessage="No plans found. Click 'Add Sermon Plan' to create a plan from a YouTube sermon."
            showSearch={true}
            searchPlaceholder="Search plans by name or description..."
          />
        )}
      </div>

      {/* Sermon Plan (YouTube-based) creation modal */}
      <SermonPlanModal
        church={church}
        typeofentity={typeofentity}
        isOpen={isSermonModalOpen}
        onClose={() => setIsSermonModalOpen(false)}
        onSuccess={() => setReloadData((prev) => !prev)}
      />

      {/* Existing Plan editor modal for edits */}
      <PlanModal
        church={church}
        typeofentity={typeofentity}
        isOpen={isPlanModalOpen}
        onClose={() => {
          setIsPlanModalOpen(false);
          setSelectedRow(null);
          setReloadData((prev) => !prev);
        }}
        rowData={selectedRow}
        onSuccess={() => {
          setReloadData((prev) => !prev);
        }}
      />

      {/* Global Sermon Study Attachment Modal */}
      <ReactModal
        isOpen={isTWGModalOpen}
        onRequestClose={handleCloseTWGModal}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 1000,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#1f1f1f",
            border: "none",
            borderRadius: "12px",
            width: "80%",
            maxHeight: "80vh",
            padding: "0",
          },
        }}
      >
        <div className="flex flex-col h-full text-white">
          <div className="flex justify-between items-center p-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold">Attach Global Sermon Studies</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCloseTWGModal}
                className="px-5 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSelectedPlans}
                disabled={selectedTWGPlans.length === 0 || isSavingLinks}
                className="px-6 py-2 bg-yellowish hover:bg-yellow-500 text-black rounded-xl transition-colors font-bold disabled:opacity-50"
              >
                {isSavingLinks ? "Attaching..." : `Attach (${selectedTWGPlans.length})`}
              </button>
              <button
                onClick={handleCloseTWGModal}
                className="p-1 hover:bg-gray-700 rounded-full transition-colors"
              >
                <IoCloseOutline className="text-2xl" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto">
            {isFetchingTWG ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellowish"></div>
              </div>
            ) : twgPlans.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                No completed global sermon studies available.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {twgPlans.map((plan) => {
                  const thumbnailUrl = getSermonStudyThumbnailUrl(plan);
                  const isSelected = selectedTWGPlans.includes(plan._id);

                  return (
                    <div
                      key={plan._id}
                      onClick={() => togglePlanSelection(plan)}
                      className={`relative rounded-xl overflow-hidden border-2 bg-[#2a2a2a] transition-all transform ${plan.isAttachedToCurrentEntity ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:scale-[1.02]"} ${
                        isSelected
                          ? "border-yellowish ring-2 ring-yellowish/50"
                          : "border-transparent hover:border-gray-600"
                      }`}
                    >
                      <div className="relative h-36 w-full bg-blackish">
                        <img
                          src={thumbnailUrl || sermonStudyPlaceholderImage}
                          alt={plan.planName || "Global Sermon Study"}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.src = sermonStudyPlaceholderImage;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-lg mb-1 truncate">
                          {plan.planName || "Untitled Plan"}
                        </h3>
                        <p className="text-gray-400 text-sm line-clamp-2">
                          {plan.finalLogMessage ||
                            plan.description ||
                            plan.planDescription ||
                            "Completed global sermon study."}
                        </p>
                        <div className="mt-3 flex items-center gap-2 text-xs">
                          <span className="px-2 py-1 rounded-full bg-black/40 text-gray-300">{plan.lifecycleStatus || plan.status || "COMPLETED"}</span>
                          {plan.isAttachedToCurrentEntity && (
                            <span className="px-2 py-1 rounded-full bg-green-700/50 text-green-100">Already attached</span>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-yellowish text-black rounded-full p-1">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </ReactModal>
    </div>
  );
};

export default SermonPlans;
